import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { MCQOption } from "../content-types";
import { Explanation, Markup, OptionRow } from "./shared";
import { stableShuffle } from "./mcq-shuffle";
import type { QuestionRendererProps } from "./types";

interface CauseEffectContent {
  cause_statement?: string;
  effect_statement?: string;
  options: MCQOption[];
  correct_option_id: string;
  explanation?: string;
}

const LETTERS = "ABCDEFGH";

/** cause_effect — the two statements shown as labelled cards joined by a chain
 *  link, then "What is the relationship between these statements?" + options.
 *  (Web mirror of iOS `CauseEffectView`; the generic MCQ path just concatenated
 *  the statements, which read as an incomplete prompt with no connector.) */
export function CauseEffectRenderer({
  itemId,
  content,
  answer,
  setAnswer,
  revealed,
}: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as CauseEffectContent;
  const rawOptions: MCQOption[] = c.options ?? [];
  const options = useMemo(
    () => stableShuffle(rawOptions, itemId || c.cause_statement || ""),
    [rawOptions, itemId, c.cause_statement],
  );
  const selected = answer as string | undefined;

  const stateFor = (id: string): "neutral" | "selected" | "correct" | "wrong" => {
    if (!revealed) return selected === id ? "selected" : "neutral";
    if (id === c.correct_option_id) return "correct";
    if (selected === id) return "wrong";
    return "neutral";
  };

  return (
    <div>
      <StatementCard label={t("Statement A")} text={c.cause_statement} />
      <div className="my-2 flex justify-center text-lg text-ink-faint" aria-hidden>
        🔗
      </div>
      <StatementCard label={t("Statement B")} text={c.effect_statement} />

      <div className="mt-4 whitespace-pre-wrap text-lg font-semibold leading-snug text-ink">
        {t("What is the relationship between these statements?")}
      </div>

      <div className="mt-4 space-y-2">
        {options.map((o, i) => (
          <OptionRow
            key={o.id}
            state={stateFor(o.id)}
            disabled={revealed}
            onClick={() => !revealed && setAnswer(o.id)}
          >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-black/5 text-xs font-bold text-ink-soft">
              {LETTERS[i] ?? o.id}
            </span>
            <span className="text-ink">
              <Markup>{o.text}</Markup>
            </span>
          </OptionRow>
        ))}
      </div>

      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}

function StatementCard({ label, text }: { label: string; text?: string }) {
  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-3">
      <div className="text-[11px] font-bold uppercase tracking-wide text-brand-600">{label}</div>
      <div className="mt-1 text-sm font-medium text-brand-900">
        <Markup>{text || "—"}</Markup>
      </div>
    </div>
  );
}
